import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FilterHeader from "@/components/assistentes/FilterHeader";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableRow from "@/components/ui/shared/table/TableRow";
import clsx from "clsx";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import { Switch } from "@/components/ui/switch";
import { useAssistants } from "@/hooks/useAssistants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EllipsisVertical, Settings, Trash2 } from "lucide-react";

const Assistentes: React.FC = () => {
  const navigate = useNavigate();
  const { assistants, loading, error, toggleAssistantStatus, deleteAssistant } = useAssistants();
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const handleConfigure = (id: string) => {
    setOpenPopoverId(null);
    navigate(`/assistentes/editar/${id}`);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    await toggleAssistantStatus(id, !currentStatus);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este assistente?')) {
      await deleteAssistant(id);
    }
  };

  if (loading) {
    return (
      <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
        <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Assistentes</h2>
        <FilterHeader />
        <div className="flex-1 min-h-0 mt-3 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
            <p className="text-neutral-50">Carregando assistentes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
        <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Assistentes</h2>
        <FilterHeader />
        <div className="flex-1 min-h-0 mt-3 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar assistentes</h3>
            <p className="text-neutral-300 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
      <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Assistentes</h2>
      <FilterHeader />
      <div className="flex-1 min-h-0 mt-3">
        {assistants.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
            <div className="flex flex-col items-center gap-4 text-center p-6">
              <div className="w-16 h-16 bg-neutral-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-50 mb-2">Nenhum assistente encontrado</h3>
                <p className="text-neutral-300 text-sm">
                  Crie seu primeiro assistente para começar a automatizar atendimentos.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea
            classNames={{
              root: "w-full h-full overflow-x-auto border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500",
            }}
          >
            <Table className="min-w-[350px]">
              <TableHeader className="border-b-[1px] border-[#3F3F464D]">
                <TableRow>
                  <TableCell className="text-neutral-50 font-semibold">
                    <p className="py-2">Assistente</p>
                  </TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Status</TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Estado</TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Ações</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assistants.map((assistant) => {
                  return (
                    <TableRow key={assistant.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <p className="font-medium">{assistant.name}</p>
                          <p className="text-xs text-neutral-400">
                            {assistant.model} • {assistant.response_time_value} {assistant.response_time_unit}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-col">
                          <div className="flex justify-start items-center gap-2">
                            <div
                              className={clsx(
                                "w-[7px] h-[7px] rounded-full",
                                assistant.is_active ? "bg-green-500" : "bg-orange-400"
                              )}
                            ></div>
                            <p>{assistant.is_active ? "Ativo" : "Inativo"}</p>
                          </div>
                          <p className="text-xs text-neutral-400">
                            Máx. {assistant.max_messages} msg
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={assistant.is_active}
                          onCheckedChange={(checked) => handleToggleStatus(assistant.id, assistant.is_active)}
                          activeColor="data-[state=checked]:bg-[#22c55e]"
                          inactiveColor="data-[state=unchecked]:bg-[#3F3F46]"
                          thumbColor="bg-white"
                        />
                      </TableCell>
                      <TableCell as="td">
                        <Popover
                          open={openPopoverId === assistant.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? assistant.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button className="w-[34px] h-[34px] flex justify-center items-center border-[1px] border-[#3F3F464D] bg-neutral-600 rounded-[8px]">
                              <EllipsisVertical size={20} color="#FAFAFA" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="!bg-[#18181B] p-1 w-[130px] flex flex-col gap-1 border-[#3F3F464D] border">
                            <button
                              onClick={() => handleConfigure(assistant.id)}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-[#27272A] min-h-[32px] text-neutral-50"
                            >
                              <Settings size={16} />
                              Configurar
                            </button>
                            <div className="h-px bg-[#3F3F464D] mx-1" />
                            <button
                              onClick={() => {
                                setOpenPopoverId(null);
                                handleDelete(assistant.id);
                              }}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-[#27272A] min-h-[32px] text-red-400"
                            >
                              <Trash2 size={16} />
                              Remover
                            </button>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default Assistentes;

